import type { BlobUploadProgress } from '../../api/blobUpload';
import { sedrApi, type SedrFile } from '../../api/sedr';
import { encryptAndUploadSedrEvidence } from '../../crypto/sedrEvidenceFile';
import { inferContentTypeFromFileName } from './sedrPreview';

export interface UploadSedrEvidenceFilesInput {
  roomId: string;
  files: SedrFile[];
  parentId: string | null;
  selected: File[];
  roomKey: Uint8Array;
  onProgress?: (progress: BlobUploadProgress) => void;
}

export interface UploadSedrEvidenceFilesResult {
  files: SedrFile[];
  uploadedFileIds: string[];
}

/**
 * Encrypts and registers one or more evidence files under `parentId`.
 * Returns the refreshed room file list and ids of the newly created files.
 */
export async function uploadSedrEvidenceFiles(
  input: UploadSedrEvidenceFilesInput,
): Promise<UploadSedrEvidenceFilesResult> {
  const { roomId, parentId, selected, roomKey, onProgress } = input;
  let workingFiles = input.files;
  const uploadedFileIds: string[] = [];

  for (const file of selected) {
    const uploaded = await encryptAndUploadSedrEvidence(file, roomKey, onProgress);
    const existing = workingFiles.find(
      (f) =>
        !f.isDirectory &&
        (f.parentId ?? null) === (parentId ?? null) &&
        f.name === file.name &&
        !f.versionOfFileId,
    );
    const created = await sedrApi.addFile(roomId, {
      name: file.name,
      isDirectory: false,
      parentId,
      blobId: uploaded.blobId,
      contentType: file.type || inferContentTypeFromFileName(file.name) || 'application/octet-stream',
      sizeBytes: uploaded.sizeBytes,
      wrappedContentKey: uploaded.wrappedContentKey,
      encryptionFileId: uploaded.encryptionFileId,
      contentHash: uploaded.contentHash,
      chunkPlaintextBytes: uploaded.chunkPlaintextBytes,
      chunkCount: uploaded.chunkCount,
      chunkCiphertextSizes: uploaded.chunkCiphertextSizes,
      versionOfFileId: existing?.id ?? null,
      versionNumber: existing ? (existing.versionNumber ?? 1) + 1 : 1,
    });
    uploadedFileIds.push(created.id);
    workingFiles = [...workingFiles.filter((f) => f.id !== created.id), created];
  }

  const files = await sedrApi.listFiles(roomId);
  return { files, uploadedFileIds };
}
